import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EMAIL_TEMPLATE_OPTIONS, EmailTemplateType } from 'src/app/Constant/email-template.constants';
import { buildEmailTemplatePreviewHtml } from 'src/app/Services/email-template-preview.util';
import { NotificationProfileService } from 'src/app/Services/notification-profile.service';
import { SlackIntegrationService } from 'src/app/Services/slack-integration.service';


interface Profile {
  id: string;
  name: string;
  type: 'email' | 'slack';
  isPrimary: boolean;
  emails?: string[];
  template?: EmailTemplateType;
  workspace?: string;
  workspaceName?: string;
  channels?: string[];
  channelIds?: string[];
}

interface Workspace {
  id: string;
  name: string;
}

interface Channel {
  id: string;
  name: string;
  isPrivate: boolean;
}

@Component({
  selector: 'app-notification-profile-view',
  templateUrl: './notification-profile-view.component.html',
  styleUrls: ['./notification-profile-view.component.scss']
})
export class NotificationProfileViewComponent implements OnInit {
  profiles: Profile[] = [];
  modalVisible = false;
  currentProfileIndex: number | null = null;
  newEmail = '';
  isLoading = false;
  
  profileForm: Profile = this.getEmptyProfile();

  workspaces: Workspace[] = [];
  channels: Channel[] = [];
  availableChannels: string[] = [];
  selectedChannels: string[] = [];

  readonly emailTemplateOptions = EMAIL_TEMPLATE_OPTIONS;
  private readonly templatePreviewCache = new Map<EmailTemplateType, SafeHtml>();

  constructor(private notificationProfileService: NotificationProfileService,
    private slackIntegrationService: SlackIntegrationService,
    private sanitizer: DomSanitizer
  ) {}
  
  ngOnInit(): void {
    this.fetchProfiles();
  }

  nameError = false;
  emailsError = false;
  channelsError = false;
    
  fetchWorkspaces(): void {
    this.isLoading = true;
    this.getWorkspacesFromApi()
      .subscribe({
        next: (workspaces) => {
          this.workspaces = workspaces;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching workspaces:', error);
          
          this.workspaces = [];
          this.isLoading = false;
        }
      });
  }

  fetchProfiles(): void {
    this.isLoading = true;
    this.getProfilesFromApi().subscribe({
      next: (response) => {
        
        this.profiles = response.map((profile: any) => ({
          id: this.generateId(),
          name: profile.profile_name,
          type: profile.profile_type === 'slack_notification' ? 'slack' : 'email',
          isPrimary: profile.is_primary,
          emails: profile.emails || [],
          template: profile.template_type || 'DEFAULT',
          workspace: profile.workspace_id,
          workspaceName: profile.workspace_name,
          channels: profile.channels ? profile.channels.map((channel: any) => channel.channel_name) : [],
          channelIds: profile.channels ? profile.channels.map((channel: any) => channel.channel_id) : []
        }));
        this.isLoading = false;
      },
      error: () => {
        this.profiles = [];
        this.isLoading = false;
      }
    });
  }
  
  getProfilesFromApi(): Observable<Profile[]> {
    return this.notificationProfileService.getNotificationProfile()
      .pipe(
        map(response => response.data || []),
        catchError(error => {
          console.error('Error fetching profiles:', error);
          return of([]);
        })
      );
  }
  
  getWorkspacesFromApi(): Observable<Workspace[]> {
    return this.slackIntegrationService.getSlackWorkspaces().pipe(
        map(response => {
          if (response && response.data) {
            return response.data.map(workspace => ({
              id: workspace.workspace_id,
              name: workspace.workspace_name
            }));
          }
          return [];
        }),
        catchError(error => {
          console.error('API error:', error);
          return of([]);
        })
      );
  }
  
  generateId(): string {
    return 'prof' + Math.random().toString(36).substring(2, 5);
  }
  
  getEmptyProfile(): Profile {
    return {
      id: this.generateId(),
      name: '',
      type: 'email',
      isPrimary: false,
      emails: [],
      template: 'DEFAULT',
      channels: [],
      channelIds: []
    };
  }

  // Cached per template type (only 5 possible values) so the preview iframe's
  // `srcdoc` binding sees a stable reference across change-detection ticks
  // instead of reloading on every cycle.
  previewHtmlFor(templateType: EmailTemplateType): SafeHtml {
    let cached = this.templatePreviewCache.get(templateType);
    if (!cached) {
      cached = this.sanitizer.bypassSecurityTrustHtml(buildEmailTemplatePreviewHtml(templateType));
      this.templatePreviewCache.set(templateType, cached);
    }
    return cached;
  }

  getTemplateLabel(templateType: EmailTemplateType | undefined): string {
    const match = this.emailTemplateOptions.find(opt => opt.id === templateType);
    return match ? match.label : 'Standard';
  }

  // Preview iframe starts at a reasonable guess and grows to fit its actual
  // content on load, so the modal itself is the only scroll container --
  // no scrollbar-within-a-scrollbar.
  templatePreviewHeight = 300;

  onTemplatePreviewLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    const doc = iframe.contentDocument || (iframe.contentWindow ? iframe.contentWindow.document : null);
    const height = doc && doc.documentElement ? doc.documentElement.scrollHeight : 0;
    if (height) {
      this.templatePreviewHeight = height + 20;
    }
  }

  openProfileModal(): void {
    this.profileForm = this.getEmptyProfile();
    this.newEmail = '';
    this.modalVisible = true;
    this.currentProfileIndex = null;
    this.selectedChannels = [];
    this.availableChannels = [];
  }

  saveProfile(): void {
    // Reset error flags
    this.nameError = false;
    this.emailsError = false;
    this.channelsError = false;
  
    // Validate name
    if (!this.profileForm.name || !this.profileForm.name.trim()) {
      this.nameError = true;
    }
  
    // Validate emails for email type
    if (this.profileForm.type === 'email') {
      if (!this.profileForm.emails || this.profileForm.emails.length === 0) {
        this.emailsError = true;
      } else {
        const hasInvalidEmail = this.profileForm.emails.some(
          email => !this.isValidEmail(email)
        );
        if (hasInvalidEmail) {
          this.emailsError = true;
        }
      }
    }
  
    // Validate channels for slack type
    if (this.profileForm.type === 'slack') {
      if (!this.selectedChannels || this.selectedChannels.length === 0) {
        this.channelsError = true;
      }
    }
  
    // Stop if any validation failed
    if (this.nameError || this.emailsError || this.channelsError) {
      return;
    }
  
    const profileToSave: Profile = { ...this.profileForm };
  
    if (profileToSave.type === 'slack' && profileToSave.workspace) {
      const selectedWorkspace = this.workspaces.find(w => w.id === profileToSave.workspace);
      if (selectedWorkspace) {
        profileToSave.workspaceName = selectedWorkspace.name;
      }
    }
  
    if (profileToSave.isPrimary) {
      this.profiles.forEach(p => p.isPrimary = false);
    }
  
    const payload: any = {
      profile_type: profileToSave.type === 'email' ? 'email_notification' : 'slack_notification',
      profile_name: profileToSave.name,
      is_primary: profileToSave.isPrimary
    };
  
    if (profileToSave.type === 'email') {
      payload.emails = profileToSave.emails || [];
      payload.template_type = profileToSave.template || 'DEFAULT';
    } else if (profileToSave.type === 'slack') {
      const selectedWorkspace = this.workspaces.find(w => w.id === profileToSave.workspace);
      const selectedChannelObjects = this.selectedChannels.map(name => {
        const matched = this.channels.find(c => c.name === name);
        return matched ? { channel_name: matched.name, channel_id: matched.id } : null;
      }).filter(c => c !== null);
  
      payload.workspace_id = selectedWorkspace.id;
      payload.workspace_name = selectedWorkspace.name;
      payload.channels = selectedChannelObjects;
    }
  
    const isEdit = this.currentProfileIndex !== null;
  
    const saveObservable = isEdit
      ? this.notificationProfileService.updateNotificationProfile(payload)
      : this.notificationProfileService.addNotificationProfile(payload);
  
    saveObservable.subscribe({
      next: (response) => {
        if (isEdit) {
          this.profiles[this.currentProfileIndex!] = profileToSave;
        } else {
          this.profiles.push(profileToSave);
        }
        this.closeModal();
      },
      error: (error) => {
        console.error('Failed to save profile', error);
      }
    });
  }
  
  closeModal(): void {
    this.modalVisible = false;
  }
  
  addEmailToForm(): void {
  if (!this.newEmail || !this.newEmail.trim()) {
    return;
  }

  const email = this.newEmail.trim();

  if (!this.isValidEmail(email)) {
    this.emailsError = true;
    return;
  }

  this.emailsError = false;

  if (!this.profileForm.emails) {
    this.profileForm.emails = [];
  }

  // Prevent duplicates (optional but recommended)
  if (!this.profileForm.emails.includes(email)) {
    this.profileForm.emails.push(email);
  }

  this.newEmail = '';
}

  
  removeEmail(index: number): void {
    if (this.profileForm.emails) {
      this.profileForm.emails.splice(index, 1);
    }
  }
  

  deleteProfile(index: number): void {
    const profileToDelete = this.profiles[index];
    
    const payload = profileToDelete.name;
    
      
      
      this.notificationProfileService.deleteNotificationProfile(payload).subscribe({
        
        next: (response) => {
          this.profiles.splice(index, 1);
          this.isLoading = true;
          if (profileToDelete.isPrimary && this.profiles.length > 0) {
            this.setPrimary(this.profiles[0]);
          }
          
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to delete profile', error);
          this.isLoading = false;
        }
      });
    
  }

  removeProfile(index: number): void {
    this.deleteProfile(index);
  }
  
  editProfile(index: number): void {
    this.profileForm = JSON.parse(JSON.stringify(this.profiles[index]));
    this.modalVisible = true;
    this.currentProfileIndex = index;
    
    if (this.profileForm.type === 'slack' && this.profileForm.workspace) {
      this.selectedChannels = this.profileForm.channels || [];
      this.fetchChannels(this.profileForm.workspace);
    }
  }

  setPrimary(selectedProfile: Profile): void {
  this.profiles.forEach(p => p.isPrimary = false);
  selectedProfile.isPrimary = true;

  const payload: any = {
    profile_type: selectedProfile.type === 'email' ? 'email_notification' : 'slack_notification',
    profile_name: selectedProfile.name,
    is_primary: true
  };

  if (selectedProfile.type === 'email') {
    payload.emails = selectedProfile.emails || [];
    payload.template_type = selectedProfile.template || 'DEFAULT';
  } else if (selectedProfile.type === 'slack') {
    const selectedWorkspace = this.workspaces.find(w => w.id === selectedProfile.workspace);
    
    let selectedChannelObjects = [];
    
    if (selectedProfile.channels && selectedProfile.channelIds && 
        selectedProfile.channels.length === selectedProfile.channelIds.length) {
      selectedChannelObjects = selectedProfile.channels.map((name, index) => ({
        channel_name: name,
        channel_id: selectedProfile.channelIds[index]
      }));
    }
    else if (selectedProfile.channels) {
      selectedChannelObjects = selectedProfile.channels.map(name => ({
        channel_name: name,
        channel_id: name
      }));
    }

    payload.workspace_id = selectedProfile.workspace;
    payload.workspace_name = selectedWorkspace ? selectedWorkspace.name : selectedProfile.workspaceName;
    payload.channels = selectedChannelObjects;
  }

  this.notificationProfileService.updateNotificationProfile(payload).subscribe({
    next: () => {
    
    },
    error: (error) => {
      console.error('Failed to update primary profile', error);
      selectedProfile.isPrimary = false;
    }
  });
}
  
  onTypeChange(): void {
    if (this.profileForm.type === 'email') {
      this.profileForm.emails = [];
      this.profileForm.template = 'DEFAULT';
      delete this.profileForm.workspace;
      delete this.profileForm.workspaceName;
      delete this.profileForm.channels;
      delete this.profileForm.channelIds;
      this.selectedChannels = [];
    } else if (this.profileForm.type === 'slack') {
      delete this.profileForm.emails;
      this.profileForm.workspace = '';
      this.profileForm.workspaceName = '';
      this.profileForm.channels = [];
      this.profileForm.channelIds = [];
      this.selectedChannels = [];
      this.availableChannels = [];

      // Fetch workspaces only when Slack is selected
        // Fetch only if workspaces haven't already been loaded
    if (this.workspaces.length === 0) {
      this.fetchWorkspaces();
    }
    }
  }
  
  fetchChannels(workspaceId: string): void {
    if (!workspaceId) {
      this.availableChannels = [];
      return;
    }

    const selectedWorkspace = this.workspaces.find(w => w.id === workspaceId);
    if (selectedWorkspace) {
      this.profileForm.workspaceName = selectedWorkspace.name;
    }
    
    this.isLoading = true;
    this.getChannelsFromApi(workspaceId)
      .subscribe({
        next: (channels) => {
          this.channels = channels;
          this.availableChannels = channels.map(c => c.name);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching channels:', error);
          const dummyChannels = {
            'Workspace A': ['general', 'alerts', 'team-chat', 'notifications', 'errors'],
            'Workspace B': ['dev', 'qa', 'support', 'releases', 'incidents']
          };
          
          const workspaceName = this.workspaces.find(w => w.id === workspaceId).name || 'Unknown';
          this.availableChannels = dummyChannels[workspaceName] || ['general', 'random'];
          this.isLoading = false;
        }
      });
  }
  
  getChannelsFromApi(workspaceId: string): Observable<Channel[]> {
    return this.slackIntegrationService.getSlackWorkspaceChannels(workspaceId).pipe(
      map(response => {
        if (response && response["data"] && response["data"].channelList) {
          return response["data"].channelList.map(channel => ({
            id: channel.channel_id,
            name: channel.channel_name,
            isPrivate: channel.is_private === 'true'
          }));
        }
        return [];
      }),
      catchError(error => {
        console.error('API error:', error);
        return of([]);
      })
    );
  }
  
  toggleChannelSelection(channel: string): void {
    const index = this.selectedChannels.indexOf(channel);
    if (index === -1) {
      this.selectedChannels.push(channel);
    } else {
      this.selectedChannels.splice(index, 1);
    }
    
    this.profileForm.channels = [...this.selectedChannels];
    
    this.profileForm.channelIds = this.selectedChannels.map(channelName => {
      const channel = this.channels.find(c => c.name === channelName);
      return channel ? channel.id : '';
    }).filter(id => id !== '');
  }
  
  isChannelSelected(channel: string): boolean {
    return this.selectedChannels.includes(channel);
  }

  getWorkspaceName(workspaceId: string): string {
    const workspace = this.workspaces.find(w => w.id === workspaceId);
    return workspace ? workspace.name : workspaceId;
  }

  isValidEmail(email: string): boolean {
  const emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

}